import { useQuery } from '@apollo/client';
import { Columns, Column, Stack } from 'braid-design-system';
import queryString from 'query-string';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import { Error } from 'src/components/Error/Error';
import { getConfigValues } from 'src/config';
import { hasProductInfo } from 'src/data/listing-products/shared';
import {
  GetCheckoutPaymentPageDocument,
  ListingProductCode,
} from 'src/generated/graphql';
import useOverflowScroll from 'src/hooks/useOverflowScroll';
import {
  BOOST_BUNDLE_DISCOUNT_FEATURE_TOGGLE_ENABLED,
  getBoostBundlePrices,
} from 'src/utils/feature-flags';
import {
  findListingProductByProductCode,
  findListingProducts,
  initProductBundle,
  isCustomEmailBoost,
  getIsListingLus,
  isSearchBoostOnly,
} from 'src/utils/listingProducts';

import { ListingProductNotFoundError } from '../ListingProductNotFoundError';
import { PaymentContainer } from '../PaymentContainer';

import PaymentDetails from './components/PaymentDetails/PaymentDetails';
import BoostBundleProductDetails from './components/ProductDetails/BoostBundleProductDetails';
import EmailBoostProductDetails from './components/ProductDetails/EmailBoostProductDetails';
import AccelerateExperimentProductDetails from './components/ProductDetails/FreemiumExperimentProductDetails';
import ProductDetails from './components/ProductDetails/ProductDetails';
import SearchBoostProductDetails from './components/ProductDetails/SearchBoostProductDetails';

const { IS_FREEMIUM_EXPERIMENT } = getConfigValues();

const Checkout = () => {
  const location = useLocation();
  const { productToken: productTokens } = queryString.parseUrl(
    location.search,
  ).query;
  const { listingId } = queryString.parseUrl(location.search).query;

  const { data, error, loading } = useQuery(GetCheckoutPaymentPageDocument);
  const [isSearchBoostAddOnChecked, setIsSearchBoostAddOnChecked] =
    useState(false);

  useOverflowScroll();

  if (loading) {
    return (
      <PaymentContainer>
        <CustomLoader />
      </PaymentContainer>
    );
  }

  const client = data?.user?.userView?.client;
  const queryParamSelectedListingProducts = findListingProducts(
    data?.listingProducts,
    productTokens,
  );

  if (error || !data || !client) {
    return (
      <PaymentContainer>
        <Error />
      </PaymentContainer>
    );
  }

  if (!queryParamSelectedListingProducts) {
    return (
      <PaymentContainer>
        <ListingProductNotFoundError />
      </PaymentContainer>
    );
  }

  const listing = data.user?.userView?.client?.listings.find(
    (l) => l.id === Number(listingId),
  );
  const hasSearchBoosted = listing ? listing.isPremium : false;
  const isListingLus =
    listing !== undefined && getIsListingLus(data?.listingProducts, listing);

  let selectedListingProducts = [...queryParamSelectedListingProducts];
  const productBundleResult = initProductBundle(
    data.listingProducts,
    selectedListingProducts,
    hasSearchBoosted,
  );

  if (
    productBundleResult.type === 'error' ||
    isCustomEmailBoost(productBundleResult.value.info.productCode)
  ) {
    return (
      <PaymentContainer>
        <ListingProductNotFoundError />
      </PaymentContainer>
    );
  }

  const productBundle = productBundleResult.value;

  // user checks search boost & this product can be search boosted
  if (isSearchBoostAddOnChecked && productBundle.availableSearchBoost) {
    selectedListingProducts = selectedListingProducts.concat(
      productBundle.availableSearchBoost,
    );
  }

  const isBundleProduct =
    !hasSearchBoosted &&
    !productBundle.availableSearchBoost &&
    !productBundle.availableEmailBoost;

  let emailBoostDetails;
  let searchBoostDetails;
  let productDetails;
  if (isBundleProduct) {
    emailBoostDetails = (
      <BoostBundleProductDetails
        isListingLus={isListingLus}
        productBundle={productBundle}
      />
    );
  }
  // This product is email boosted
  else if (!productBundle.availableEmailBoost) {
    emailBoostDetails = (
      <EmailBoostProductDetails product={productBundle.info} />
    );
  }

  // The product is search boosted / search boost is checked
  if (
    isSearchBoostOnly(productBundle.info.productCode) ||
    isSearchBoostAddOnChecked
  ) {
    let boostCost;
    if (
      BOOST_BUNDLE_DISCOUNT_FEATURE_TOGGLE_ENABLED &&
      isSearchBoostAddOnChecked &&
      (productBundle.info.productCode === ListingProductCode.EmailBoost ||
        productBundle.info.productCode === ListingProductCode.EmailBoostLus)
    ) {
      boostCost = getBoostBundlePrices({
        isLus: isListingLus,
      }).discountedSearchBoost;
    } else {
      boostCost = productBundle.boostComponents?.search
        ? productBundle.boostComponents.search.cost
        : productBundle.info.cost;
    }

    searchBoostDetails = (
      <SearchBoostProductDetails
        // Search boost add-on is available, or itself is SearchBoost
        boostCost={boostCost}
      />
    );
  }

  if (
    (productBundle.availableSearchBoost && productBundle.availableEmailBoost) ||
    productBundle.info.productCode ===
      ListingProductCode.SixAdPackAndSearchBoost ||
    productBundle.info.productCode ===
      ListingProductCode.ListUntilSoldAndSearchBoostLus
  ) {
    const contractInfo = findListingProductByProductCode(
      data.listingProducts,
      productBundle.contract,
    );
    if (contractInfo && IS_FREEMIUM_EXPERIMENT) {
      productDetails = (
        <AccelerateExperimentProductDetails product={contractInfo} />
      );
    } else if (contractInfo && hasProductInfo(contractInfo)) {
      productDetails = <ProductDetails product={contractInfo} />;
    }
  } else {
    // No one should reach this branch
    productDetails = null;
  }

  return (
    <PaymentContainer>
      <Columns space="small" collapseBelow="desktop">
        <Column width="2/5">
          <PaymentDetails
            isListingLus={isListingLus}
            isBundleProduct={isBundleProduct}
            client={client}
            productBundle={productBundle}
            selectedProducts={selectedListingProducts}
            searchBoostChecked={isSearchBoostAddOnChecked}
            onSearchBoostToggle={() =>
              setIsSearchBoostAddOnChecked((checked) => !checked)
            }
          />
        </Column>
        <Column>
          <Stack space="small">
            {productDetails}
            {emailBoostDetails}
            {searchBoostDetails}
          </Stack>
        </Column>
      </Columns>
    </PaymentContainer>
  );
};

export default Checkout;
